import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import type {
  AdminSubscription,
  AdminSubscriptionListResponse,
  GetPortalUserDetailParams,
  ListAdminSubscriptionsParams,
  ListPortalUsersParams,
  ListPortalWorkspacesParams,
  PortalMetrics,
  PortalUsageLimitConfig,
  PortalUser,
  PortalUserDetail,
  PortalUserSubscriptionTier,
  PortalUsersPaginatedResponse,
  PortalWorkspaceBulkSettings,
  PortalWorkspaceDetail,
  PortalWorkspacesPaginatedResponse,
  UpdatePortalWorkspaceBulkSettingsParams,
} from '../types/users.types';

/** Portal user-management proxies to self-service; allow headroom above backend upstream timeout. */
const PORTAL_MANAGE_PROXY_TIMEOUT_MS = 90_000;

const portalUsersEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getPortalUsers: builder.query<PortalUsersPaginatedResponse, ListPortalUsersParams | void>({
      query: (params) => ({
        method: 'GET',
        url: 'b2b/manage/users',
        params: params ?? {},
        handleErrorGlobally: false,
        timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
      }),
      providesTags: [{ id: 'LIST', type: APITagType.PortalUsers }],
    }),
    getPortalUser: builder.query<PortalUserDetail, GetPortalUserDetailParams | string>({
      query: (arg) => {
        const params = typeof arg === 'string' ? { userId: arg } : arg;
        const { userId, page, page_size: pageSize } = params;

        return {
          method: 'GET',
          url: `b2b/manage/users/${userId}`,
          params: {
            ...(page !== undefined ? { page } : {}),
            ...(pageSize !== undefined ? { page_size: pageSize } : {}),
          },
          handleErrorGlobally: false,
          timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
        };
      },
      providesTags: (_result, _error, arg) => {
        const userId = typeof arg === 'string' ? arg : arg.userId;
        return [{ id: userId, type: APITagType.PortalUsers }];
      },
    }),
    blockPortalUser: builder.mutation<PortalUser, string>({
      query: (userId) => ({
        method: 'POST',
        url: `b2b/manage/users/${userId}/block`,
        data: {},
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, userId) => [
        { id: 'LIST', type: APITagType.PortalUsers },
        { id: userId, type: APITagType.PortalUsers },
      ],
    }),
    unblockPortalUser: builder.mutation<PortalUser, string>({
      query: (userId) => ({
        method: 'POST',
        url: `b2b/manage/users/${userId}/unblock`,
        data: {},
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, userId) => [
        { id: 'LIST', type: APITagType.PortalUsers },
        { id: userId, type: APITagType.PortalUsers },
      ],
    }),
    setPortalUserQuotaOverride: builder.mutation<
    PortalUser,
    { userId: string; creative_quota_override: number | null }
    >({
      query: ({ userId, creative_quota_override }) => ({
        method: 'PATCH',
        url: `b2b/manage/users/${userId}/quota-override`,
        data: { creative_quota_override },
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { id: 'LIST', type: APITagType.PortalUsers },
        { id: userId, type: APITagType.PortalUsers },
      ],
    }),
    setPortalUserBillingConversionDisabled: builder.mutation<
    PortalUser,
    { userId: string; disable_billing_conversion: boolean }
    >({
      query: ({ userId, disable_billing_conversion }) => ({
        method: 'PATCH',
        url: `b2b/manage/users/${userId}/billing-conversion`,
        data: { disable_billing_conversion },
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { id: 'LIST', type: APITagType.PortalUsers },
        { id: userId, type: APITagType.PortalUsers },
      ],
    }),
    setPortalUserConversionModalHidden: builder.mutation<
    PortalUser,
    { userId: string; hide_conversion_modal: boolean }
    >({
      query: ({ userId, hide_conversion_modal }) => ({
        method: 'PATCH',
        url: `b2b/manage/users/${userId}/conversion-modal`,
        data: { hide_conversion_modal },
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { id: 'LIST', type: APITagType.PortalUsers },
        { id: userId, type: APITagType.PortalUsers },
      ],
    }),
    getPortalUsageLimitConfig: builder.query<PortalUsageLimitConfig, void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/manage/usage-limit-config',
        handleErrorGlobally: false,
      }),
      providesTags: [{ id: 'CONFIG', type: APITagType.PortalUsageLimitConfig }],
    }),
    updatePortalUsageLimitConfig: builder.mutation<PortalUsageLimitConfig, number>({
      query: (monthlyCreativeLimit) => ({
        method: 'PATCH',
        url: 'b2b/manage/usage-limit-config',
        data: { monthly_creative_limit: monthlyCreativeLimit },
        handleErrorGlobally: false,
      }),
      invalidatesTags: [{ id: 'CONFIG', type: APITagType.PortalUsageLimitConfig }],
    }),
    getPortalMetrics: builder.query<PortalMetrics, void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/manage/metrics',
        handleErrorGlobally: false,
        timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
      }),
      providesTags: [{ id: 'METRICS', type: APITagType.PortalMetrics }],
    }),
    getPortalSubscription: builder.query<AdminSubscription, string>({
      query: (workspaceId) => ({
        method: 'GET',
        url: `b2b/manage/subscriptions/${workspaceId}`,
        handleErrorGlobally: false,
      }),
      providesTags: (_result, _error, workspaceId) => [
        { id: workspaceId, type: APITagType.PortalSubscriptions },
      ],
    }),
    listPortalSubscriptions: builder.query<AdminSubscriptionListResponse, ListAdminSubscriptionsParams | void>({
      query: (params) => ({
        method: 'GET',
        url: 'b2b/manage/subscriptions',
        params: params ?? {},
        handleErrorGlobally: false,
        timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
      }),
      providesTags: [{ id: 'LIST', type: APITagType.PortalSubscriptions }],
    }),
    updatePortalSubscriptionTier: builder.mutation<
    AdminSubscription,
    { workspaceId: string; tier: PortalUserSubscriptionTier }
    >({
      query: ({ workspaceId, tier }) => ({
        method: 'PATCH',
        url: `b2b/manage/subscriptions/${workspaceId}`,
        data: { tier },
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { id: workspaceId, type: APITagType.PortalSubscriptions },
        { id: workspaceId, type: APITagType.PortalWorkspaces },
        // Invalidate all user caches so open drawers pick up the new tier.
        { type: APITagType.PortalUsers },
        { id: 'LIST', type: APITagType.PortalSubscriptions },
        { id: 'LIST', type: APITagType.PortalWorkspaces },
      ],
    }),
    getPortalWorkspaces: builder.query<PortalWorkspacesPaginatedResponse, ListPortalWorkspacesParams | void>({
      query: (params) => ({
        method: 'GET',
        url: 'b2b/manage/workspaces',
        params: params ?? {},
        handleErrorGlobally: false,
        timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
      }),
      providesTags: [{ id: 'LIST', type: APITagType.PortalWorkspaces }],
    }),
    getPortalWorkspace: builder.query<PortalWorkspaceDetail, string>({
      query: (workspaceId) => ({
        method: 'GET',
        url: `b2b/manage/workspaces/${workspaceId}`,
        handleErrorGlobally: false,
        timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
      }),
      providesTags: (_result, _error, workspaceId) => [
        { id: workspaceId, type: APITagType.PortalWorkspaces },
      ],
    }),
    getPortalWorkspaceBulkSettings: builder.query<PortalWorkspaceBulkSettings, string>({
      query: (workspaceId) => ({
        method: 'GET',
        url: `b2b/manage/workspaces/${workspaceId}/bulk-settings`,
        handleErrorGlobally: false,
      }),
      providesTags: (_result, _error, workspaceId) => [
        { id: `${workspaceId}-bulk-settings`, type: APITagType.PortalWorkspaces },
      ],
    }),
    updatePortalWorkspaceBulkSettings: builder.mutation<
    PortalWorkspaceBulkSettings,
    UpdatePortalWorkspaceBulkSettingsParams
    >({
      query: ({ workspaceId, ...data }) => ({
        method: 'PATCH',
        url: `b2b/manage/workspaces/${workspaceId}/bulk-settings`,
        data,
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { id: workspaceId, type: APITagType.PortalWorkspaces },
        { id: `${workspaceId}-bulk-settings`, type: APITagType.PortalWorkspaces },
        { id: 'LIST', type: APITagType.PortalWorkspaces },
        { id: 'LIST', type: APITagType.PortalUsers },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetPortalUsersQuery,
  useGetPortalUserQuery,
  useBlockPortalUserMutation,
  useUnblockPortalUserMutation,
  useSetPortalUserQuotaOverrideMutation,
  useSetPortalUserBillingConversionDisabledMutation,
  useSetPortalUserConversionModalHiddenMutation,
  useGetPortalUsageLimitConfigQuery,
  useUpdatePortalUsageLimitConfigMutation,
  useGetPortalMetricsQuery,
  useGetPortalSubscriptionQuery,
  useListPortalSubscriptionsQuery,
  useUpdatePortalSubscriptionTierMutation,
  useGetPortalWorkspacesQuery,
  useGetPortalWorkspaceQuery,
  useGetPortalWorkspaceBulkSettingsQuery,
  useUpdatePortalWorkspaceBulkSettingsMutation,
} = portalUsersEndpoints;

export function isPortalSyncNotConfiguredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const responseData = (error as { responseData?: Record<string, unknown> }).responseData;
  return responseData?.error_code === 'portal_sync_not_configured';
}
